import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import exitLettersService from '../../services/exitLettersService';
import employeeService from '../../services/employeeService';
import NotificationToast from '../../components/ui/NotificationToast';

const ExitLetters = () => {
  const { userProfile } = useAuth();
  const canManageSensitive = userProfile?.role === 'super_admin';
  const [letters, setLetters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [editingLetter, setEditingLetter] = useState(null);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: '', letter_type: 'experience', resignation_date: '', last_working_date: '',
    relieving_date: '', reason: '', content: '', status: 'draft'
  });

  // Cleanup blob URL when preview closes or component unmounts
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    loadLetters();
    loadEmployees();
  }, []);

  const loadLetters = async () => {
    try {
      setLoading(true);
      const result = await exitLettersService.getExitLetters();
      if (result?.success) setLetters(result.data || []);
    } catch (error) {
      console.error('Error loading exit letters:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    const result = await employeeService.getEmployees();
    if (result?.success) setEmployees(result.data || []);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canManageSensitive) {
      setNotification({ message: 'You do not have permission to manage exit letters.', type: 'error' });
      return;
    }
    try {
      const result = editingLetter
        ? await exitLettersService.updateExitLetter(editingLetter.id, formData)
        : await exitLettersService.createExitLetter(formData);
      
      if (result?.success) {
        setNotification({ message: `Exit letter ${editingLetter ? 'updated' : 'created'} successfully`, type: 'success' });
        setShowModal(false);
        setEditingLetter(null);
        setFormData({ employee_id: '', letter_type: 'experience', resignation_date: '', last_working_date: '', relieving_date: '', reason: '', content: '', status: 'draft' });
        loadLetters();
      } else {
        setNotification({ message: result?.error || 'Operation failed', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    }
  };

  const handleEdit = (letter) => {
    if (!canManageSensitive) {
      setNotification({ message: 'You do not have permission to edit exit letters.', type: 'error' });
      return;
    }
    setEditingLetter(letter);
    setFormData({
      employee_id: letter.employee_id || '',
      letter_type: letter.letter_type || 'experience',
      resignation_date: letter.resignation_date || '',
      last_working_date: letter.last_working_date || '',
      relieving_date: letter.relieving_date || '',
      reason: letter.reason || '',
      content: letter.content || '',
      status: letter.status || 'draft'
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!canManageSensitive) {
      setNotification({ message: 'You do not have permission to delete exit letters.', type: 'error' });
      return;
    }
    if (!window.confirm('Are you sure you want to delete this exit letter?')) return;
    try {
      const result = await exitLettersService.deleteExitLetter(id);
      if (result?.success) {
        setNotification({ message: 'Exit letter deleted successfully', type: 'success' });
        loadLetters();
      } else {
        setNotification({ message: result?.error || 'Failed to delete', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    }
  };

  const generateTemplate = async () => {
    if (!formData.employee_id || !formData.letter_type || !formData.last_working_date) {
      setNotification({ message: 'Please fill in Employee, Letter Type, and Last Working Date first', type: 'error' });
      return;
    }

    setGeneratingPreview(true);
    try {
      const employee = employees.find(e => e.id === formData.employee_id);
      const tempData = {
        ...formData,
        employee_name: employee?.full_name || null,
        position: employee?.position || null,
        emp_code: employee?.employee_id || null, // ensure we supply employee_id to standard templates correctly
      };

      const result = await exitLettersService.previewExitLetter({
        ...tempData,
        content: '' // Let backend generate
      });

      if (result?.success && result.data?.content) {
        setFormData(prev => ({ ...prev, content: result.data.content }));
        setNotification({ message: 'Professional template generated successfully', type: 'success' });
      } else {
        const errorMsg = result?.error || result?.message || 'Failed to generate template';
        setNotification({ message: errorMsg, type: 'error' });
      }
    } catch (error) {
      const errorMsg = error?.message || String(error) || 'Error generating template';
      setNotification({ message: errorMsg, type: 'error' });
    } finally {
      setGeneratingPreview(false);
    }
  };

  const handlePreview = async () => {
    if (!formData.employee_id || !formData.letter_type || !formData.last_working_date) {
      setNotification({ message: 'Please fill in Employee, Letter Type, and Last Working Date first', type: 'error' });
      return;
    }

    setGeneratingPreview(true);
    try {
      const employee = employees.find(e => e.id === formData.employee_id);
      const tempData = {
        ...formData,
        employee_name: employee?.full_name || null,
        position: employee?.position || null,
        emp_code: employee?.employee_id || null,
      };

      const result = await exitLettersService.previewExitLetter({
        ...tempData,
        content: formData.content || '' // Force regeneration if empty
      });

      if (result?.success && result.data?.content) {
        const content = result.data.content || '';
        setPreviewContent(content);
        
        const cleanContent = content
          .replace(/\/\*# sourceMappingURL=.*?\*\//g, '') 
          .replace(/\/\/# sourceMappingURL=.*?$/gm, '') 
          .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ''); 
        
        const blob = new Blob([cleanContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setShowPreview(true);
      } else {
        const errorMsg = result?.error || result?.message || 'Failed to generate preview';
        setNotification({ message: errorMsg, type: 'error' });
      }
    } catch (error) {
      const errorMsg = error?.message || String(error) || 'Error generating preview';
      setNotification({ message: errorMsg, type: 'error' });
    } finally {
      setGeneratingPreview(false);
    }
  };

  const handleDownload = async (id) => {
    try {
      const result = await exitLettersService.downloadExitLetter(id);
      if (result?.success) {
        setNotification({ message: 'Exit letter downloaded successfully', type: 'success' });
      } else {
        setNotification({ message: 'Error downloading exit letter', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'Error downloading exit letter', type: 'error' });
    }
  };

  return (
    <div className="p-6">
      <NotificationToast message={notification?.message} type={notification?.type} onDismiss={() => setNotification(null)} />
      
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Exit Letters</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage employee offboarding</p>
        </div>
        <Button
          onClick={() => { setEditingLetter(null); setFormData({ employee_id: '', letter_type: 'experience', resignation_date: '', last_working_date: '', relieving_date: '', reason: '', content: '', status: 'draft' }); setShowModal(true); }}
          disabled={!canManageSensitive}
          title={canManageSensitive ? 'Create exit letter' : 'Only super admins can create exit letters'}
        >
          <Icon name="Plus" size={16} className="mr-2" />
          Create Exit Letter
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" /><p className="text-muted-foreground">Loading...</p></div>
      ) : (
        <div className="bg-background border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Letter Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Resignation Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Last Working Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Relieving Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {letters.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-muted-foreground">
                      <Icon name="FileText" size={48} className="mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium mb-2">No exit letters found</p>
                      <p className="text-sm">Create your first exit letter using the button above</p>
                    </td>
                  </tr>
                ) : (
                  letters.map(letter => (
                    <tr key={letter.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-foreground">{letter.employee_name || 'N/A'}</div>
                        {letter.employee_email && (
                          <div className="text-sm text-muted-foreground">{letter.employee_email}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800 capitalize">
                          {letter.letter_type || 'experience'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">
                        {letter.resignation_date ? new Date(letter.resignation_date).toLocaleDateString('en-IN') : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">
                        {letter.last_working_date ? new Date(letter.last_working_date).toLocaleDateString('en-IN') : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">
                        {letter.relieving_date ? new Date(letter.relieving_date).toLocaleDateString('en-IN') : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          letter.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                          letter.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                          letter.status === 'accepted' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {letter.status?.charAt(0).toUpperCase() + letter.status?.slice(1) || 'Draft'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">
                        {letter.created_at ? new Date(letter.created_at).toLocaleDateString('en-IN') : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleDownload(letter.id)} title="Download">
                            <Icon name="Download" size={14} />
                          </Button>
                          {canManageSensitive && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(letter)} title="Edit">
                                <Icon name="Edit" size={14} />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(letter.id)} className="text-destructive" title="Delete">
                                <Icon name="Trash2" size={14} />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowModal(false); setEditingLetter(null); }}>
          <div className="bg-background rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">{editingLetter ? 'Edit' : 'Create'} Exit Letter</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Employee *</label>
                <select value={formData.employee_id} onChange={(e) => setFormData({...formData, employee_id: e.target.value})} className="w-full px-3 py-2 border border-border rounded-md" required>
                  <option value="">Select Employee</option>
                  {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.full_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Letter Type *</label>
                <select value={formData.letter_type} onChange={(e) => setFormData({...formData, letter_type: e.target.value})} className="w-full px-3 py-2 border border-border rounded-md" required>
                  <option value="resignation">Resignation</option>
                  <option value="termination">Termination</option>
                  <option value="experience">Experience Letter</option>
                  <option value="relieving">Relieving Letter</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {formData.letter_type === 'resignation' && <Input label="Resignation Date" type="date" value={formData.resignation_date} onChange={(e) => setFormData({...formData, resignation_date: e.target.value})} />}
                <Input label="Last Working Date *" type="date" value={formData.last_working_date} onChange={(e) => setFormData({...formData, last_working_date: e.target.value})} required />
                {(formData.letter_type === 'relieving' || formData.letter_type === 'experience') && <Input label="Relieving Date" type="date" value={formData.relieving_date} onChange={(e) => setFormData({...formData, relieving_date: e.target.value})} />}
              </div>
              <textarea value={formData.reason} onChange={(e) => setFormData({...formData, reason: e.target.value})} className="w-full px-3 py-2 border border-border rounded-md" rows="2" placeholder="Reason for exit" />
              <div>
                <label className="block text-sm font-medium mb-2">Letter Content</label>
                <div className="flex gap-2 mb-2">
                  <Button type="button" variant="outline" size="sm" onClick={generateTemplate} disabled={generatingPreview}>
                    <Icon name="FileText" size={14} className="mr-2" />
                    {generatingPreview ? 'Generating...' : 'Generate Template'}
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={handlePreview} disabled={generatingPreview}>
                    <Icon name="Eye" size={14} className="mr-2" />
                    Preview
                  </Button>
                </div>
                <textarea value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} className="w-full px-3 py-2 border border-border rounded-md" rows="6" placeholder="Click 'Generate Template' to create a professional letter template, or write your own content. You can edit the generated template as needed." />
              </div>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); setEditingLetter(null); }}>Cancel</Button>
                <Button type="submit" disabled={generatingPreview}>
                  {generatingPreview ? 'Saving...' : 'Save Letter'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowPreview(false); if (previewUrl && previewUrl.startsWith('blob:')) { URL.revokeObjectURL(previewUrl); setPreviewUrl(''); } }}>
          <div className="bg-background rounded-lg p-6 max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Exit Letter Preview</h3>
              <Button variant="ghost" size="sm" onClick={() => { setShowPreview(false); if (previewUrl && previewUrl.startsWith('blob:')) { URL.revokeObjectURL(previewUrl); setPreviewUrl(''); } }}>
                <Icon name="X" size={16} />
              </Button>
            </div>
            <div className="flex-1 overflow-auto border border-border rounded">
              <iframe
                src={previewUrl}
                className="w-full h-full min-h-[600px] border-0"
                title="Exit Letter Preview"
                sandbox="allow-same-origin"
                id="exit-letter-iframe"
              />
            </div>
            <div className="mt-4 flex gap-3 justify-end">
              <Button variant="outline" onClick={() => { setShowPreview(false); if (previewUrl && previewUrl.startsWith('blob:')) { URL.revokeObjectURL(previewUrl); setPreviewUrl(''); } }}>Close</Button>
              <Button onClick={() => {
                if (previewContent) {
                  const printWindow = window.open('', '_blank');
                  if (printWindow) {
                    const cleanContent = previewContent
                      .replace(/\/\*# sourceMappingURL=.*?\*\//g, '')
                      .replace(/\/\/# sourceMappingURL=.*?$/gm, '')
                      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
                    
                    const contentWithScript = cleanContent.replace(
                      '</head>',
                      `<style>
                        @media print {
                          body { margin: 0; padding: 0; }
                        }
                      </style>
                    </head>`
                    );
                    
                    printWindow.document.write(contentWithScript);
                    printWindow.document.close();
                    printWindow.focus();
                    printWindow.onload = () => { setTimeout(() => { printWindow.print(); }, 500); };
                  }
                } else {
                  const iframe = document.getElementById('exit-letter-iframe');
                  if (iframe && iframe.contentWindow) { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
                }
              }}>
                <Icon name="Printer" size={16} className="mr-2" />
                Print / Save as PDF
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExitLetters;

